import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Badge,
  Card,
  CardContent,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  QuestionAnswer as QuestionAnswerIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Close as CloseIcon
} from '@mui/icons-material';

// Platform logoları için import'lar (şimdilik icon'lar kullanacağız)
import StorefrontIcon from '@mui/icons-material/Storefront'; // N11
import { n11Api } from '../../../config/apiClient';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag'; // Trendyol
import LocalMallIcon from '@mui/icons-material/LocalMall'; // Hepsiburada
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'; // Amazon
import { useTranslation } from 'react-i18next';

interface ProductQuestion {
  id: string;
  platform: 'n11' | 'trendyol' | 'hepsiburada' | 'amazon';
  productId: string;
  productName: string;
  question: string;
  questionSubject?: string;
  answer?: string;
  customerName: string;
  questionDate: string;
  answerDate?: string;
  status: 'pending' | 'answered' | 'rejected';
  isPublic: boolean;
}

interface ProductQuestionsManagementProps {
  companyId: string | null;
}

const ProductQuestionsManagement: React.FC<ProductQuestionsManagementProps> = ({ companyId }) => {
  const { t } = useTranslation('salesSystem');
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState<ProductQuestion[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<ProductQuestion | null>(null);
  const [questionDetailModalOpen, setQuestionDetailModalOpen] = useState(false);

  // Platform bilgileri
  const platforms = [
    { 
      id: 'trendyol', 
      name: 'Trendyol', 
      icon: ShoppingBagIcon, 
      color: '#f27a1a',
      enabled: true // Tüm sekmeler aktif
    },
    { 
      id: 'hepsiburada', 
      name: 'Hepsiburada', 
      icon: LocalMallIcon, 
      color: '#ff6000',
      enabled: true // Tüm sekmeler aktif
    },
    { 
      id: 'n11', 
      name: 'N11', 
      icon: StorefrontIcon, 
      color: '#7b68ee',
      enabled: true // N11 aktif
    },
    { 
      id: 'amazon', 
      name: 'Amazon', 
      icon: ShoppingCartIcon, 
      color: '#ff9900',
      enabled: true // Tüm sekmeler aktif
    }
  ];

  const currentPlatform = platforms[activeTab];

  // Mock data - gerçek API entegrasyonu sonrası kaldırılacak
  const mockQuestions: ProductQuestion[] = [
    {
      id: '1',
      platform: 'n11',
      productId: 'PRD001',
      productName: 'Samsung Galaxy S21',
      question: 'Bu ürünün garantisi kaç yıl?',
      customerName: 'Ahmet Y.',
      questionDate: '2024-01-15T10:30:00Z',
      status: 'pending',
      isPublic: true
    },
    {
      id: '2',
      platform: 'n11',
      productId: 'PRD002',
      productName: 'iPhone 13 Pro',
      question: 'Kargo ücreti dahil mi?',
      answer: 'Evet, kargo ücreti dahildir.',
      customerName: 'Fatma K.',
      questionDate: '2024-01-14T14:20:00Z',
      answerDate: '2024-01-14T16:45:00Z',
      status: 'answered',
      isPublic: true
    }
  ];

  useEffect(() => {
    if (companyId) {
      loadQuestions();
    }
  }, [companyId, activeTab]);

  const loadQuestions = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Şimdilik sadece N11 için gerçek API kullanıyoruz
      if (currentPlatform.id === 'n11' && companyId) {
        // N11 sorularını çek (cache'den veya API'den)
        // Şimdilik boş başlıyoruz, kullanıcı "Platform Sorularını Çek" butonuna basacak
        setQuestions([]);
      } else {
        // Diğer platformlar için mock data
        const platformQuestions = mockQuestions.filter(q => q.platform === currentPlatform.id);
        setQuestions(platformQuestions);
      }
      
    } catch (err) {
      console.error('Sorular yüklenirken hata:', err);
      setError(t('ecommerceQuestions.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSyncQuestions = async () => {
    if (!companyId) {
      setError(t('ecommerceQuestions.errors.companyMissing'));
      return;
    }

    setSyncing(true);
    setError(null);

    try {
      console.log('🔄 [QUESTIONS SYNC] Platform sorularını çekme başlatılıyor...');
      
      // Şimdilik sadece N11 için çalışacak
      if (currentPlatform.id === 'n11') {
        // N11 API çağrısı - OrdersManagement.tsx ile aynı şekilde
        console.log('🔗 [QUESTIONS SYNC] Company ID:', companyId);
        
        const response = await n11Api.post('/api/n11/questions/sync', {
          productId: '',
          status: '',
          startDate: '',
          endDate: '',
          currentPage: 0,
          pageSize: 100
        });

        const data = response.data;
        
        if (!data.success) {
          throw new Error(data.error || t('ecommerceQuestions.errors.n11FetchFailed'));
        }

        console.log('✅ [QUESTIONS SYNC] N11 soruları başarıyla senkronize edildi:', {
          questionCount: data.questions?.length || 0,
          pagingData: data.pagingData
        });

        // Çekilen soruları işle ve state'e kaydet
        if (data.questions && data.questions.length > 0) {
          const processedQuestions = data.questions.map((q: any) => ({
            id: q.id,
            platform: 'n11' as const,
            productId: q.productId,
            productName: q.productTitle || 'Bilinmeyen Ürün',
            question: q.question || '',
            questionSubject: q.questionSubject || undefined, // Soru başlığı
            answer: q.answer || undefined,
            customerName: 'N11 Müşterisi', // N11 API'sinde müşteri adı yok
            questionDate: new Date().toISOString(), // N11 API'sinde tarih yok, şimdilik current time
            answerDate: q.answer ? new Date().toISOString() : undefined,
            status: q.answer ? 'answered' as const : 'pending' as const,
            isPublic: true
          }));

          setQuestions(processedQuestions);
        } else {
          setQuestions([]);
        }
        
      } else {
        throw new Error(t('ecommerceQuestions.errors.integrationNotReady', { platform: currentPlatform.name }));
      }
      
    } catch (err) {
      console.error('❌ [QUESTIONS SYNC] Soru senkronizasyonu hatası:', err);
      setError(err instanceof Error ? err.message : t('ecommerceQuestions.errors.unknown'));
    } finally {
      setSyncing(false);
    }
  };

  const getStatusChip = (status: ProductQuestion['status']) => {
    switch (status) {
      case 'pending':
        return <Chip icon={<ScheduleIcon />} label={t('ecommerceQuestions.status.pending')} color="warning" size="small" />;
      case 'answered':
        return <Chip icon={<CheckCircleIcon />} label={t('ecommerceQuestions.status.answered')} color="success" size="small" />;
      case 'rejected':
        return <Chip icon={<ErrorIcon />} label={t('ecommerceQuestions.status.rejected')} color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('tr-TR');
  };

  const getQuestionCounts = () => {
    return {
      total: questions.length,
      pending: questions.filter(q => q.status === 'pending').length,
      answered: questions.filter(q => q.status === 'answered').length,
      rejected: questions.filter(q => q.status === 'rejected').length
    };
  };

  const counts = getQuestionCounts();

  const handleQuestionDetailOpen = (question: ProductQuestion) => {
    setSelectedQuestion(question);
    setQuestionDetailModalOpen(true);
  };

  const handleQuestionDetailClose = () => {
    setSelectedQuestion(null);
    setQuestionDetailModalOpen(false);
  };


  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <QuestionAnswerIcon sx={{ fontSize: 32, mr: 1.5, color: 'primary.main' }} />
          <Typography variant="h5" component="h1" fontWeight="bold">
            {t('ecommerceQuestions.title')}
          </Typography>
        </Box>
        
        <Button
          variant="contained"
          color="primary"
          startIcon={syncing ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={handleSyncQuestions}
          disabled={syncing}
        >
          {syncing ? t('ecommerceQuestions.actions.fetching') : t('ecommerceQuestions.actions.fetchPlatformQuestions')}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Platform Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {platforms.map((platform, index) => {
            const Icon = platform.icon;
            // Gerçek sorulardan sayıyı al (N11 için), diğerleri için mock data
            const platformQuestions = platform.id === 'n11' 
              ? questions.filter(q => q.platform === platform.id)
              : mockQuestions.filter(q => q.platform === platform.id);
            const pendingCount = platformQuestions.filter(q => q.status === 'pending').length;
            
            return (
              <Tab
                key={platform.id}
                icon={
                  <Badge badgeContent={pendingCount} color="error">
                    <Icon sx={{ color: platform.color }} />
                  </Badge>
                }
                label={platform.name}
                disabled={!platform.enabled}
                sx={{
                  '&.Mui-disabled': {
                    opacity: 0.5
                  }
                }}
              />
            );
          })}
        </Tabs>
      </Paper>

      {/* İstatistik Kartları */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary" fontWeight="bold">
                {counts.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('ecommerceQuestions.stats.total')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="warning.main" fontWeight="bold">
                {counts.pending}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('ecommerceQuestions.stats.pending')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main" fontWeight="bold">
                {counts.answered}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('ecommerceQuestions.stats.answered')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="error.main" fontWeight="bold">
                {counts.rejected}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('ecommerceQuestions.stats.rejected')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Platform Durumu - Sadece N11 dışındakiler için uyarı */}
      {currentPlatform.id !== 'n11' && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>{currentPlatform.name}</strong> {t('ecommerceQuestions.integrationNotReadyLine1')} {t('ecommerceQuestions.integrationNotReadyLine2')}
          </Typography>
        </Alert>
      )}

      {/* Sorular Tablosu */}
      <Paper>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <currentPlatform.icon sx={{ mr: 1, color: currentPlatform.color }} />
            {t('ecommerceQuestions.platformQuestionsTitle', { platform: currentPlatform.name })}
            {loading && <CircularProgress size={20} sx={{ ml: 2 }} />}
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : questions.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <QuestionAnswerIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('ecommerceQuestions.empty.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {currentPlatform.id === 'n11'
                ? t('ecommerceQuestions.empty.n11Description', { platform: currentPlatform.name })
                : t('ecommerceQuestions.empty.demoDescription', { platform: currentPlatform.name })
              }
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('ecommerceQuestions.table.product')}</TableCell>
                  <TableCell>{t('ecommerceQuestions.table.question')}</TableCell>
                  <TableCell>{t('ecommerceQuestions.table.customer')}</TableCell>
                  <TableCell>{t('ecommerceQuestions.table.date')}</TableCell>
                  <TableCell>{t('ecommerceQuestions.table.status')}</TableCell>
                  <TableCell align="center">{t('ecommerceQuestions.table.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {questions.map((question) => (
                  <TableRow key={question.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {question.productName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {question.productId}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 300 }}>
                        {question.question}
                      </Typography>
                      {question.answer && (
                        <Box sx={{ mt: 1, p: 1, bgcolor: 'success.50', borderRadius: 1 }}>
                          <Typography variant="caption" color="success.main" fontWeight="bold">
                            {t('ecommerceQuestions.answerLabel')}
                          </Typography>
                          <Typography variant="body2" color="success.main">
                            {question.answer}
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {question.customerName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(question.questionDate)}
                      </Typography>
                      {question.answerDate && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t('ecommerceQuestions.answeredAt')}: {formatDate(question.answerDate)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {getStatusChip(question.status)}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title={t('ecommerceQuestions.actions.viewDetails')}>
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleQuestionDetailOpen(question)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Soru Detay Modalı */}
      <Dialog 
        open={questionDetailModalOpen} 
        onClose={handleQuestionDetailClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <QuestionAnswerIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">{t('ecommerceQuestions.detail.title')}</Typography>
          </Box>
          <IconButton onClick={handleQuestionDetailClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent dividers>
          {selectedQuestion && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Ürün Bilgisi */}
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('ecommerceQuestions.detail.productInfo').toUpperCase()}
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {selectedQuestion.productName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('ecommerceQuestions.detail.productId')}: {selectedQuestion.productId}
                </Typography>
              </Paper>

              {/* Soru */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('ecommerceQuestions.detail.customerQuestion').toUpperCase()}
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'info.50', border: '1px solid', borderColor: 'info.200' }}>
                  {selectedQuestion.questionSubject && (
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'medium' }}>
                      {selectedQuestion.questionSubject}
                    </Typography>
                  )}
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedQuestion.question}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      {selectedQuestion.customerName} • {formatDate(selectedQuestion.questionDate)}
                    </Typography>
                    {getStatusChip(selectedQuestion.status)}
                  </Box>
                </Paper>
              </Box>

              {/* Cevap (varsa) */}
              {selectedQuestion.answer && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {t('ecommerceQuestions.detail.storeAnswer').toUpperCase()}
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200' }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {selectedQuestion.answer}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedQuestion.answerDate && `${t('ecommerceQuestions.answeredAt')}: ${formatDate(selectedQuestion.answerDate)}`}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {/* N11 Bilgilendirme */}
              {selectedQuestion.platform === 'n11' && selectedQuestion.status === 'pending' && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    <strong>{t('ecommerceQuestions.detail.n11InfoTitle')}</strong>
                  </Typography>
                  <Typography variant="body2">
                    {t('ecommerceQuestions.detail.n11InfoText')}
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleQuestionDetailClose} variant="outlined">
            {t('ecommerceQuestions.actions.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProductQuestionsManagement;
